import React, { useState } from 'react';
import { PlusCircleIcon, MinusCircleIcon } from 'lucide-react';
import Button from '../ui/Button';
import { StaffAllowance } from '../../types/staff';
interface AllowanceEditorProps {
  allowances: StaffAllowance[];
  semester: 'semester1' | 'semester2';
  onChange: (allowances: StaffAllowance[]) => void;
  availableTemplates: any[];
  /**
   * Academic year to stamp on anything created here.
   *
   * Without it a new allowance carries no year, and a year-less allowance counts
   * towards EVERY year by design (so pre-v2.11.0 records don't vanish). Adding a
   * 2027 allowance would therefore have applied it retroactively to 2026 as well.
   * See issue #245.
   */
  year?: number;
}
const AllowanceEditor: React.FC<AllowanceEditorProps> = ({
  allowances,
  semester,
  onChange,
  availableTemplates,
  year
}) => {
  const [editingAllowance, setEditingAllowance] = useState<StaffAllowance | null>(null);
  // Use templates directly - they should already be an array from DataContext
  const safeTemplates = availableTemplates || [];
  // Generate a unique ID for new allowances
  const generateAllowanceId = () => `allowance_${Date.now()}_${Math.floor(Math.random() * 1000)}`;
  // Add a new allowance
  const handleAddAllowance = () => {
    if (safeTemplates.length === 0) {
      console.warn('No allowance templates available');
      return;
    }
    const defaultTemplate = safeTemplates[0];
    const newAllowance: StaffAllowance = {
      id: generateAllowanceId(),
      templateId: defaultTemplate.id,
      name: defaultTemplate.name,
      minutes: defaultTemplate.defaultMinutes || 0,
      appliesTo: semester, // Always use the current semester tab
      ...(year != null && { year })
    };
    onChange([...allowances, newAllowance]);
  };
  // Remove an allowance
  const handleRemoveAllowance = (id: string) => {
    onChange(allowances.filter(a => a.id !== id));
  };
  // Update an allowance
  const handleUpdateAllowance = (id: string, updates: Partial<StaffAllowance>) => {
    onChange(allowances.map(allowance => allowance.id === id ? {
      ...allowance,
      ...updates
    } : allowance));
  };
  // Handle template selection change
  const handleTemplateChange = (id: string, templateId: string) => {
    const template = safeTemplates.find(t => t.id === templateId);
    if (template) {
      handleUpdateAllowance(id, {
        templateId,
        name: template.name,
        minutes: template.defaultMinutes || 0
      });
    }
  };
  // Calculate total allowance minutes
  const totalMinutes = allowances.reduce((sum, allowance) => {
    if (allowance.appliesTo === semester || allowance.appliesTo === 'both') {
      return sum + allowance.minutes;
    }
    return sum;
  }, 0);
  // Don't render if no templates are available
  if (safeTemplates.length === 0) {
    return <div className="space-y-4">
        <div className="flex justify-between items-center">
          <h4 className="text-sm font-medium text-ink-700">
            {semester === 'semester1' ? 'Semester 1' : 'Semester 2'} Allowances
          </h4>
        </div>
        <div className="text-center py-4 text-sm text-danger-fg bg-danger-bg rounded-md">
          No allowance templates available. Using default templates.
        </div>
      </div>;
  }
  return <div className="space-y-4">
      <div className="flex justify-between items-center">
        <h4 className="text-sm font-medium text-ink-700">
          {semester === 'semester1' ? 'Semester 1' : 'Semester 2'} Allowances
        </h4>
        <div className="text-sm text-ink-500">
          Total: <span className="font-medium"><span className="font-mono tabular-nums">{totalMinutes}</span> minutes</span>
        </div>
      </div>
      {allowances.length === 0 ? <div className="text-center py-4 text-sm text-ink-500 bg-ink-50 rounded-md">
          No allowances added
        </div> : <div className="space-y-2">
          {allowances.map(allowance => <div key={allowance.id} className="flex items-center space-x-2 p-2 bg-ink-50 rounded-md">
              <div className="flex-1">
                <select className="block w-full text-sm border-ink-300 rounded-md shadow-sm focus:ring-navy-500 focus:border-navy-500" value={allowance.templateId} onChange={e => handleTemplateChange(allowance.id, e.target.value)}>
                  {safeTemplates.map(template => <option key={template.id} value={template.id}>
                      {template.name}
                    </option>)}
                </select>
              </div>
              <div className="w-24">
                <input type="number" min="0" max="440" step="5" className="block w-full text-sm border-ink-300 rounded-md shadow-sm focus:ring-navy-500 focus:border-navy-500" value={allowance.minutes} onChange={e => handleUpdateAllowance(allowance.id, {
            minutes: parseInt(e.target.value) || 0
          })} />
              </div>
              <div className="w-20">
                <span className="text-sm text-ink-500 font-medium">
                  {semester === 'semester1' ? 'S1' : 'S2'}
                </span>
              </div>
              <button type="button" onClick={() => handleRemoveAllowance(allowance.id)} className="text-danger-fg hover:text-danger-fg">
                <MinusCircleIcon className="h-5 w-5" />
              </button>
            </div>)}
        </div>}
      <div className="pt-2">
        <Button variant="secondary" size="sm" leftIcon={<PlusCircleIcon className="h-4 w-4" />} onClick={handleAddAllowance}>
          Add Allowance
        </Button>
      </div>
    </div>;
};
export default AllowanceEditor;